/**
 * Conversational revision merge (services/aiRevise.ts).
 *
 * Applies a revised GeneratedStack (from reviseDiagramWithAI) onto the live
 * canvas while preserving everything the model never saw: node positions,
 * user styling, non-AI node types (shapes etc.), and canvas-only callbacks.
 *
 * Matching is by id — the revise prompt (buildRevisePrompt) forbids the model
 * from renaming ids. What the model returns and the canvas already has is
 * updated in place; new ids are materialized + laid out below the existing
 * content (the Editor's GAP=80 append pattern); existing ids missing from the
 * revised stack are deleted along with their edges. Groups are reconciled by
 * flattening to absolute positions and re-wrapping via withGroupContainers.
 *
 * SAFETY VALVE — `lowMatch`: when fewer than half of the revised node ids
 * match existing canvas tech nodes (or the model returns zero nodes for a
 * non-empty canvas), the model almost certainly rewrote the diagram instead
 * of revising it. The merge result is still computed and returned, but the
 * caller should fall back to a full replace (materializeGeneratedStack in
 * replace mode) instead of applying it.
 */
import { MarkerType, type Edge, type Node } from 'reactflow';
import type { GeneratedEdge, GeneratedStack } from '../types';
import {
  absoluteRects,
  deriveHandles,
  layoutElements,
  toAbsolutePositions,
  withGroupContainers,
  type GroupLayoutSpec,
  type GroupRect,
  type LayoutDirection,
} from './layoutService';

export interface MergeRevisedResult {
  nodes: Node[];
  edges: Edge[];
  /** New nodes materialized from the revised stack (tech + sticky + text). */
  added: number;
  /** Existing nodes whose data was refreshed from the revised stack (regardless of diff). */
  updated: number;
  /** Existing content nodes (tech/sticky/text) deleted because the revised stack dropped them. */
  removed: number;
  /**
   * Refreshed nodes whose visible content actually differed (label, icon,
   * description, category, importance or group). A model that echoes the
   * canvas back scores 0 here — the honest "nothing changed" signal.
   */
  changed: number;
  /** Model-managed connections added or removed by the revision. */
  edgesAdded: number;
  edgesRemoved: number;
  /** Group containers added, dissolved, or renamed by the revision. */
  groupsAdded: number;
  groupsRemoved: number;
  groupsUpdated: number;
  /** Labels of the nodes the revision added (tech + sticky + text, stack order). */
  addedLabels: string[];
  /** Labels of the content nodes the revision removed. */
  removedLabels: string[];
  /**
   * True when the revised stack matches less than half of the canvas by id —
   * the model rewrote instead of revising. The result is still returned;
   * callers should prefer a full replace when this is set.
   */
  lowMatch: boolean;
}

/** Canvas node types the AI round-trip manages. Anything else is preserved untouched. */
const AI_MANAGED_TYPES = new Set(['techNode', 'stickyNote', 'textNode', 'groupNode']);

/** Same relationship-type → stroke mapping the Editor's materializer uses. */
const EDGE_TYPE_STYLES: Record<string, { stroke: string; strokeWidth: number; strokeDasharray?: string }> = {
  data: { stroke: '#3b82f6', strokeWidth: 2 },
  api: { stroke: '#10b981', strokeWidth: 2 },
  event: { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '5 5' },
  dependency: { stroke: '#64748b', strokeWidth: 1 },
  control: { stroke: '#ef4444', strokeWidth: 2 },
};

/** Vertical gap between existing content and the cluster of new nodes. */
const GAP = 80;

const edgeKey = (e: { source: string; target: string; label?: unknown }): string =>
  `${e.source}→${e.target}::${typeof e.label === 'string' ? e.label : ''}`;

/**
 * Merge a revised GeneratedStack into the existing canvas. See the module
 * header for the matching rules and the `lowMatch` safety valve.
 */
export function mergeRevisedStack(
  revised: GeneratedStack,
  existingNodes: Node[],
  existingEdges: Edge[],
  direction: LayoutDirection = 'TB'
): MergeRevisedResult {
  const timestamp = Date.now();

  // Effective group per canvas node, captured BEFORE flattening —
  // toAbsolutePositions strips parentNode, and data.groupId alone is
  // unreliable on older canvases where membership lived only in parentNode.
  const canvasGroupById = new Map<string, string | undefined>();
  for (const n of existingNodes) {
    if (n.type === 'techNode') {
      canvasGroupById.set(
        n.id,
        (n.data?.groupId as string | undefined) ?? (n.parentNode as string | undefined)
      );
    }
  }

  // 1. Flatten parentNode sub-flows — group membership is reconciled later
  // from the revised groups, so all positioning happens in absolute coords.
  const flat = toAbsolutePositions(existingNodes);
  const flatById = new Map(flat.map((n) => [n.id, n]));

  const revisedNodesById = new Map(revised.nodes.map((n) => [n.id, n]));
  const revisedStickyById = new Map((revised.stickyNotes ?? []).map((n) => [n.id, n]));
  const revisedTextById = new Map((revised.textNodes ?? []).map((n) => [n.id, n]));
  const revisedGroups = (revised.groups ?? []).filter(
    (g) => g && g.label && Array.isArray(g.nodeIds)
  );
  // Group membership per the revised stack — drives data.groupId and re-wrap.
  const memberToGroup = new Map<string, string>();
  for (const g of revisedGroups) {
    for (const id of g.nodeIds) {
      if (!memberToGroup.has(id)) memberToGroup.set(id, g.id);
    }
  }
  const previousGroups = flat.filter((n) => n.type === 'groupNode');
  const revisedGroupIds = new Set(revisedGroups.map((g) => g.id));
  const groupsAdded = revisedGroups.filter((g) => flatById.get(g.id)?.type !== 'groupNode').length;
  const groupsRemoved = previousGroups.filter((g) => !revisedGroupIds.has(g.id)).length;
  const groupsUpdated = revisedGroups.filter(
    (g) => flatById.get(g.id)?.type === 'groupNode' && flatById.get(g.id)?.data?.label !== g.label
  ).length;

  // Safety valve: a revise should keep most ids. Below 50% the model rewrote
  // the diagram; applying the merge would scramble the user's layout.
  const existingTechCount = flat.filter((n) => n.type === 'techNode').length;
  const matchedTechCount = revised.nodes.filter((n) => flatById.get(n.id)?.type === 'techNode').length;
  const lowMatch =
    existingTechCount > 0 &&
    (revised.nodes.length === 0 || matchedTechCount / revised.nodes.length < 0.5);

  // 2 + 4. Partition existing nodes: containers are always rebuilt from the
  // revised groups; content nodes the model dropped are deleted; survivors
  // keep their object (position, styling, callbacks) with data refreshed.
  let updated = 0;
  let changed = 0;
  let removed = 0;
  const removedLabels: string[] = [];
  const keptNodes: Node[] = [];
  for (const node of flat) {
    if (node.type === 'groupNode') continue; // rebuilt in step 5
    if (!AI_MANAGED_TYPES.has(node.type ?? '')) {
      keptNodes.push(node); // shapes / unknown: the model never saw them
      continue;
    }
    if (node.type === 'techNode') {
      const rn = revisedNodesById.get(node.id);
      if (!rn) {
        removed++;
        if (node.data?.label) removedLabels.push(String(node.data.label));
        continue;
      }
      updated++;
      const nextGroupId = memberToGroup.get(node.id) ?? rn.groupId;
      // Visible-content diff: what the merge actually rewrites on screen.
      // Group membership is compared against the effective pre-flatten group.
      const effectiveGroupId = canvasGroupById.get(node.id);
      const differs =
        rn.label !== node.data.label ||
        rn.icon !== node.data.icon ||
        rn.description !== node.data.description ||
        rn.category !== node.data.category ||
        rn.importance !== node.data.importance ||
        nextGroupId !== effectiveGroupId;
      if (differs) changed++;
      keptNodes.push({
        ...node,
        data: {
          ...node.data,
          label: rn.label,
          icon: rn.icon,
          // Keep iconSlug in sync with the namespaced icon (materialize parity).
          iconSlug: rn.icon?.startsWith('si:') ? rn.icon.substring(3) : rn.icon,
          description: rn.description,
          category: rn.category,
          importance: rn.importance,
          groupId: nextGroupId,
          metadata: rn.metadata,
          layout: rn.layout,
        },
      });
      continue;
    }
    if (node.type === 'stickyNote') {
      const rn = revisedStickyById.get(node.id);
      if (!rn) {
        removed++;
        if (node.data?.label) removedLabels.push(String(node.data.label));
        continue;
      }
      updated++;
      if (rn.label !== node.data.label || (rn.color ?? node.data?.color) !== node.data?.color) {
        changed++;
      }
      keptNodes.push({
        ...node,
        data: { ...node.data, label: rn.label, color: rn.color ?? node.data?.color },
      });
      continue;
    }
    if (node.type === 'textNode') {
      const rn = revisedTextById.get(node.id);
      if (!rn) {
        removed++;
        if (node.data?.label) removedLabels.push(String(node.data.label));
        continue;
      }
      updated++;
      if (rn.label !== node.data.label || rn.description !== node.data.description) changed++;
      keptNodes.push({
        ...node,
        data: { ...node.data, label: rn.label, description: rn.description },
      });
      continue;
    }
  }
  const keptIds = new Set(keptNodes.map((n) => n.id));

  // 3. Materialize nodes the model added — same shapes as the Editor's
  // materializeGeneratedStack, minus onSnapshot (the caller injects it).
  const newTechNodes: Node[] = revised.nodes
    .filter((n) => !keptIds.has(n.id))
    .map((n) => ({
      id: n.id,
      type: 'techNode',
      position: { x: 0, y: 0 },
      data: {
        label: n.label,
        icon: n.icon,
        iconSlug: n.icon?.startsWith('si:') ? n.icon.substring(3) : n.icon,
        description: n.description,
        category: n.category,
        importance: n.importance,
        groupId: memberToGroup.get(n.id) ?? n.groupId,
        metadata: n.metadata,
        layout: n.layout,
        isAI: true,
      },
    }));
  const newStickyNodes: Node[] = (revised.stickyNotes ?? [])
    .filter((n) => !keptIds.has(n.id))
    .map((note) => ({
      id: note.id,
      type: 'stickyNote',
      position: { x: 100, y: 100 },
      data: { label: note.label || 'Note', color: note.color || 'yellow' },
      style: { width: 200, height: 160 },
    }));
  const newTextNodes: Node[] = (revised.textNodes ?? [])
    .filter((n) => !keptIds.has(n.id))
    .map((t) => ({
      id: t.id,
      type: 'textNode',
      position: { x: 100, y: 100 },
      data: { label: t.label, description: t.description },
    }));
  const materializedNew = [...newTechNodes, ...newStickyNodes, ...newTextNodes];
  const added = materializedNew.length;
  const addedLabels = materializedNew
    .map((n) => (typeof n.data?.label === 'string' ? n.data.label : ''))
    .filter((label) => label !== '');

  // Lay out ONLY the new cluster and drop it below the existing content —
  // the Editor's append-mode pattern (GAP=80, left-aligned with content).
  let offsetNew: Node[] = [];
  if (materializedNew.length > 0) {
    const newIdSet = new Set(materializedNew.map((n) => n.id));
    const layoutEdges: Edge[] = revised.edges
      .filter((e) => newIdSet.has(e.source) || newIdSet.has(e.target))
      .map((e, i) => ({ id: `revise_layout_${timestamp}_${i}`, source: e.source, target: e.target }));
    const { nodes: laidNew } = layoutElements(materializedNew, layoutEdges, [], direction);

    const nodeH = (n: Node) =>
      n.height ?? (typeof n.style?.height === 'number' ? n.style.height : 100);
    const existMinX = keptNodes.length ? Math.min(...keptNodes.map((n) => n.position.x)) : 0;
    const existMaxY = keptNodes.length
      ? Math.max(...keptNodes.map((n) => n.position.y + nodeH(n)))
      : 0;
    const newMinX = Math.min(...laidNew.map((n) => n.position.x));
    const newMinY = Math.min(...laidNew.map((n) => n.position.y));
    const dx = existMinX - newMinX;
    const dy = keptNodes.length ? existMaxY + GAP - newMinY : 0;
    offsetNew = laidNew.map((n) => ({
      ...n,
      position: { x: n.position.x + dx, y: n.position.y + dy },
    }));
  }

  const combined = [...keptNodes, ...offsetNew];
  const combinedIds = new Set(combined.map((n) => n.id));

  // 5. Reconcile groups: re-wrap from absolute positions using the revised
  // group specs. Members that moved groups land in the new container; groups
  // the model deleted simply produce no spec and their members stay top-level.
  let finalNodes = combined;
  const groupSpecs: GroupLayoutSpec[] = revisedGroups
    .map((g) => ({
      id: g.id,
      label: g.label,
      nodeIds: g.nodeIds.filter((id) => combinedIds.has(id)),
    }))
    .filter((g) => g.nodeIds.length > 0);
  if (groupSpecs.length > 0) {
    const rects = absoluteRects(combined);
    const groupRects = new Map<string, GroupRect>();
    for (const spec of groupSpecs) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const id of spec.nodeIds) {
        const r = rects.get(id);
        if (!r) continue;
        minX = Math.min(minX, r.x);
        minY = Math.min(minY, r.y);
        maxX = Math.max(maxX, r.x + r.width);
        maxY = Math.max(maxY, r.y + r.height);
      }
      if (Number.isFinite(minX)) {
        groupRects.set(spec.id, { x: minX, y: minY, width: maxX - minX, height: maxY - minY });
      }
    }
    finalNodes = withGroupContainers(combined, groupSpecs, groupRects);
    // withGroupContainers creates fresh containers — carry over canvas-only
    // extras (onSnapshot, locked) from a previous container with the same id.
    finalNodes = finalNodes.map((n) => {
      if (n.type !== 'groupNode') return n;
      const prev = flatById.get(n.id);
      return prev?.type === 'groupNode' ? { ...n, data: { ...prev.data, label: n.data?.label } } : n;
    });
  }
  const finalNodeIds = new Set(finalNodes.map((n) => n.id));

  // 6. Rebuild edges from the revised stack. An existing edge with the same
  // source+target+label keeps its id, routing type, styling and label data —
  // the user's manual tweaks survive a revision. Edges the model never saw
  // (anything touching a non-tech node, e.g. shapes) pass through untouched
  // as long as their endpoints survive.
  const existingByKey = new Map<string, Edge>();
  for (const e of existingEdges) {
    const key = edgeKey(e);
    if (!existingByKey.has(key)) existingByKey.set(key, e);
  }
  const rebuiltEdges: Edge[] = [];
  revised.edges.forEach((e: GeneratedEdge, i: number) => {
    if (!finalNodeIds.has(e.source) || !finalNodeIds.has(e.target)) return;
    const prev = existingByKey.get(edgeKey(e));
    const typeStyle = EDGE_TYPE_STYLES[e.type ?? 'data'] ?? EDGE_TYPE_STYLES.data;
    const style = prev?.style ?? typeStyle;
    const stroke = (style as { stroke?: string }).stroke ?? typeStyle.stroke;
    rebuiltEdges.push({
      ...prev,
      id: prev?.id ?? `e_${timestamp}_${i}`,
      source: e.source,
      target: e.target,
      label: e.label,
      labelStyle: prev?.labelStyle ?? { fill: '#374151', fontSize: 12 },
      labelBgStyle: prev?.labelBgStyle ?? { fill: '#f9fafb', stroke: '#e5e7eb' },
      animated: prev?.animated ?? e.type === 'event',
      type: prev?.type ?? 'smoothstep',
      style,
      markerEnd: prev?.markerEnd ?? { type: MarkerType.ArrowClosed, color: stroke },
      markerStart: e.bidirectional
        ? prev?.markerStart ?? { type: MarkerType.ArrowClosed, color: stroke }
        : undefined,
    });
  });
  const techIdsFinal = new Set(
    finalNodes.filter((n) => n.type === 'techNode').map((n) => n.id)
  );
  const passthroughEdges = existingEdges.filter((e) => {
    const bothTech = techIdsFinal.has(e.source) && techIdsFinal.has(e.target);
    if (bothTech) return false; // model-visible: the revised stack decides
    return finalNodeIds.has(e.source) && finalNodeIds.has(e.target);
  });
  const existingTechIds = new Set(
    flat.filter((n) => n.type === 'techNode').map((n) => n.id)
  );
  const existingModelEdges = existingEdges.filter(
    (e) => existingTechIds.has(e.source) && existingTechIds.has(e.target)
  );
  const existingEdgeKeys = new Set(existingModelEdges.map(edgeKey));
  const finalEdgeKeys = new Set(rebuiltEdges.map(edgeKey));
  const edgesAdded = rebuiltEdges.filter((e) => !existingEdgeKeys.has(edgeKey(e))).length;
  const edgesRemoved = existingModelEdges.filter((e) => !finalEdgeKeys.has(edgeKey(e))).length;
  let finalEdges = [...rebuiltEdges, ...passthroughEdges];

  // Handles are derived from FINAL geometry — model/parser guesses are never
  // trusted. Only the rebuilt edges are rewritten; passthrough user edges keep
  // their handles (the Editor's append-mode rule).
  const rebuiltIds = new Set(rebuiltEdges.map((e) => e.id));
  const handleMap = deriveHandles(
    finalEdges.filter((e) => rebuiltIds.has(e.id)),
    finalNodes,
    direction
  );
  if (handleMap.size > 0) {
    finalEdges = finalEdges.map((e) => {
      const h = handleMap.get(e.id);
      return h ? { ...e, sourceHandle: h.sourceHandle, targetHandle: h.targetHandle } : e;
    });
  }

  return {
    nodes: finalNodes,
    edges: finalEdges,
    added,
    updated,
    removed,
    changed,
    edgesAdded,
    edgesRemoved,
    groupsAdded,
    groupsRemoved,
    groupsUpdated,
    addedLabels,
    removedLabels,
    lowMatch,
  };
}
